/** @format */

/**
 * Utility functions for capturing screenshots from maps
 */

interface ScreenshotOptions {
  width?: number;
  height?: number;
  quality?: number; // 0-1 for JPEG quality
  format?: "png" | "jpeg";
}

/**
 * Captures a screenshot from a Mapbox GL JS map
 */
export const captureMapboxScreenshot = (
  map: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  options: ScreenshotOptions = {},
): Promise<string | null> => {
  return new Promise((resolve) => {
    try {
      if (!map?.getCanvas) {
        console.warn("Invalid Mapbox map instance");
        resolve(null);
        return;
      }

      const {
        width = 800,
        height = 600,
        quality = 0.8,
        format = "jpeg",
      } = options;

      // Get the map canvas
      const canvas = map.getCanvas();
      if (!canvas) {
        console.warn("Could not get map canvas");
        resolve(null);
        return;
      }

      // Create a new canvas with desired dimensions
      const screenshotCanvas = document.createElement("canvas");
      screenshotCanvas.width = width;
      screenshotCanvas.height = height;
      const ctx = screenshotCanvas.getContext("2d");

      if (!ctx) {
        console.warn("Could not get canvas context");
        resolve(null);
        return;
      }

      // Draw the map canvas onto our screenshot canvas
      ctx.drawImage(canvas, 0, 0, width, height);

      // Convert to base64
      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
      const base64String = screenshotCanvas.toDataURL(mimeType, quality);

      resolve(base64String);
    } catch (error) {
      console.error("Error capturing Mapbox screenshot:", error);
      resolve(null);
    }
  });
};

/**
 * Captures a screenshot from a Cesium viewer
 */
export const captureCesiumScreenshot = (
  viewer: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  options: ScreenshotOptions = {},
): Promise<string | null> => {
  return new Promise((resolve) => {
    try {
      if (!viewer?.canvas || !viewer.render) {
        console.warn("Invalid Cesium viewer instance");
        resolve(null);
        return;
      }

      const {
        width = 800,
        height = 600,
        quality = 0.8,
        format = "jpeg",
      } = options;

      // Force a render to ensure the scene is up to date
      viewer.render();

      // Get the viewer canvas
      const canvas = viewer.canvas;
      if (!canvas) {
        console.warn("Could not get Cesium canvas");
        resolve(null);
        return;
      }

      // Create a new canvas with desired dimensions
      const screenshotCanvas = document.createElement("canvas");
      screenshotCanvas.width = width;
      screenshotCanvas.height = height;
      const ctx = screenshotCanvas.getContext("2d");

      if (!ctx) {
        console.warn("Could not get canvas context");
        resolve(null);
        return;
      }

      // Draw the Cesium canvas onto our screenshot canvas
      ctx.drawImage(canvas, 0, 0, width, height);

      // Convert to base64
      const mimeType = format === "jpeg" ? "image/jpeg" : "image/png";
      const base64String = screenshotCanvas.toDataURL(mimeType, quality);

      resolve(base64String);
    } catch (error) {
      console.error("Error capturing Cesium screenshot:", error);
      resolve(null);
    }
  });
};

/**
 * Generic function to capture screenshot based on map type
 */
export const captureMapScreenshot = async (
  map: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  options: ScreenshotOptions = {},
): Promise<string | null> => {
  try {
    // Detect map type
    const isCesiumMap = map?.canvas || map?.centerCoordinates;
    const isMapboxMap = map?.getCanvas;

    if (isCesiumMap) {
      return await captureCesiumScreenshot(map, options);
    }
    if (isMapboxMap) {
      return await captureMapboxScreenshot(map, options);
    }
    console.warn("Unknown map type for screenshot capture");
    return null;
  } catch (error) {
    console.error("Error capturing map screenshot:", error);
    return null;
  }
};

/**
 * Helper function to resize base64 image
 */
export const resizeBase64Image = (
  base64String: string,
  maxWidth: number,
  maxHeight: number,
  quality = 0.8,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert back to base64
      const resizedBase64 = canvas.toDataURL("image/jpeg", quality);
      resolve(resizedBase64);
    };

    img.onerror = () => {
      reject(new Error("Failed to load image for resizing"));
    };

    img.src = base64String;
  });
};
